import { useState, useEffect } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Link, useLocation } from "react-router-dom";
import ShareThoughtModal from "../daily-overview/ShareThoughtModal";
import { NotificationCenter } from "./NotificationCenter";
import {
  LayoutDashboard,
  Calendar,
  Server,
  TrendingUp,
  Activity,
  UserCircle,
  Briefcase,
  BarChart3,
  BarChart2,
  Library,
  BookOpen,
  Link2,
  Wallet,
  Menu,
  X,
  Users,
  Target,
  Globe,
  Table,
  FileSpreadsheet,
  ListTodo,
  CreditCard,
  ShieldCheck,
  Compass,
  Archive,
} from "lucide-react";
import { getCurrentUser } from "aws-amplify/auth";
import initialTasksData from "../../data/tasks.json";

const isEspRoute = (path: string) =>
  path.includes("/email/esp-dashboard") ||
  path.includes("/email/incoming");

const isLegacyRoute = (path: string) =>
  path.includes("/email/campaigns") ||
  path.includes("/email/domain-rotation") ||
  path.includes("/email/feedblitz-domains") ||
  path.includes("/email/analytics") ||
  path.includes("/email/campaign-stats") ||
  path.includes("/email/imports") ||
  path.includes("/email/active-esps") ||
  path.includes("/converters");

const isSimplyLoanSecondaryRoute = (path: string) =>
  path.startsWith("/simplyloan/google") ||
  path.startsWith("/simplyloan/microsoft") ||
  path.startsWith("/simplyloan/optimizer");

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // All sidebar groups default to collapsed (folded in), expanding only if the active route is inside that group
  const [isCampaignHubOpen, setIsCampaignHubOpen] = useState(() => location.pathname.startsWith("/campaigns"));
  const [isEspManagementOpen, setIsEspManagementOpen] = useState(() => isEspRoute(location.pathname));
  const [isSimplyLoanOpen, setIsSimplyLoanOpen] = useState(() => location.pathname.startsWith("/simplyloan"));
  const [isSimplyLoanMoreOpen, setIsSimplyLoanMoreOpen] = useState(() => isSimplyLoanSecondaryRoute(location.pathname));
  const [isSmartResponseOpen, setIsSmartResponseOpen] = useState(() =>
    location.pathname.startsWith("/premieportalen") || location.pathname.startsWith("/smartresponse")
  );
  const [isAccountingOpen, setIsAccountingOpen] = useState(() => location.pathname.startsWith("/accounting"));
  const [isLegacyOpen, setIsLegacyOpen] = useState(() => isLegacyRoute(location.pathname));
  const [logoError, setLogoError] = useState(false);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Header State
  const [userName, setUserName] = useState("Account");
  const [userEmail, setUserEmail] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [pendingTaskCount, setPendingTaskCount] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("afima_action_center_tasks_v1");
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.filter((t: any) => t.status === "pending").length;
      }
    } catch (e) {}
    return (initialTasksData as any)?.metrics?.pendingCount || 0;
  });

  useEffect(() => {
    const updateCount = () => {
      try {
        const saved = localStorage.getItem("afima_action_center_tasks_v1");
        if (saved) {
          const parsed = JSON.parse(saved);
          setPendingTaskCount(parsed.filter((t: any) => t.status === "pending").length);
        }
      } catch (e) {}
    };
    updateCount();
    window.addEventListener("storage", updateCount);
    return () => window.removeEventListener("storage", updateCount);
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileSidebarOpen]);

  // Update Time
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      // Format: YYYY-MM-DD HH:mm:ss Europe/Copenhagen
      const timeStr = now.toLocaleString("sv-SE", {
        timeZone: "Europe/Copenhagen",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }).replace(" ", " ");
      setCurrentTime(`${timeStr} Europe/Copenhagen`);
    };
    updateTime();
  }, [location.pathname]);

  // Resolve User Name
  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        const email = user.signInDetails?.loginId || user.username || "";
        setUserEmail(email.toLowerCase());
        if (email.includes("@")) {
          const namePart = email.split("@")[0];
          setUserName(namePart.charAt(0).toUpperCase() + namePart.slice(1));
        } else if (email) {
          setUserName(email);
        }
      })
      .catch(() => setUserName("Account"));
  }, []);

  const getPageTitle = (path: string) => {
    if (path === "/overview" || path === "/ai-overview") return "Daily Overview & Agent Briefing";
    if (path === "/" || path === "/dashboard" || path === "/revenue" || path === "/wct-dashboard") return "Revenue Dashboard";
    if (path === "/api-dashboard") return "Dashboard (API)";
    if (path.startsWith("/email/dispatch-matrix") || path.startsWith("/dispatch-matrix") || path.startsWith("/sendout-plan")) return "Campaign Sendout Matrix";
    if (path.startsWith("/email/scheduler-intelligence")) return "Scheduler Intelligence";
    if (path.startsWith("/email/auto-schedule")) return "Auto-Schedule";
    if (path.startsWith("/email/campaigns")) return "Campaign Scheduler";
    if (path.startsWith("/email/domain-rotation") || path.startsWith("/email/feedblitz-domains")) return "FeedBlitz Domain Rotation";
    if (path.startsWith("/email/esp-dashboard")) return "ESP KPI Command Center";
    if (path.startsWith("/email/active-esps")) return "Active ESPs";
    if (path.startsWith("/email/imports")) return "Imports";
    if (path === "/simplyloan") return "Simplyloan Leads Overview";
    if (path.startsWith("/simplyloan/redirect-admin")) return "Redirect Admin Module";
    if (path.startsWith("/simplyloan/redirect-tracking")) return "SimplyLoan Tracking";
    if (path.startsWith("/simplyloan/meta/campaigns")) return "SimplyLoan Meta Campaign Builder";
    if (path.startsWith("/simplyloan/meta")) return "SimplyLoan Meta Ads";
    if (path.startsWith("/simplyloan/google/campaigns")) return "SimplyLoan Google Campaign Builder";
    if (path.startsWith("/simplyloan/google")) return "SimplyLoan Google Ads";
    if (path.startsWith("/simplyloan/microsoft/campaigns")) return "SimplyLoan Microsoft Campaign Builder";
    if (path.startsWith("/simplyloan/microsoft")) return "SimplyLoan Microsoft Ads";
    if (path.startsWith("/simplyloan/optimizer")) return "SimplyLoan Autonomous Marketing Agent";
    if (path.startsWith("/simplyloan/seo")) return "SimplyLoan SEO & Media Hub";
    if (path.startsWith("/simplyloan/sms")) return "SimplyLoan SMS Campaigns & AWS Scheduler";
    if (path.startsWith("/simplyloan/email")) return "Simplyloan Email Marketing";
    if (path.startsWith("/email/incoming")) return "Incoming Emails";
    if (path.startsWith("/email/analytics")) return "Engagement Analytics";
    if (path.startsWith("/converters")) return "Converter Intelligence";
    if (path.startsWith("/email/campaign-stats")) return "Campaign Stats";
    if (path.startsWith("/email/daily-reports")) return "Daily Reports";
    if (path.startsWith("/campaigns/text-library")) return "Text Library";
    if (path.startsWith("/campaigns/affiliate-links")) return "Affiliate Links";
    if (path.startsWith("/campaigns")) return "Campaign Hub";
    if (path === "/accounting/renewals") return "Upcoming Renewals & Subscriptions";
    if (path === "/accounting/invoices") return "Bilag & Invoice Matcher";
    if (path === "/accounting/esp") return "ESP Costs";
    if (path === "/accounting") return "Accounting";
    if (path === "/tasks") return "Action Center & Tasks";
    if (path.startsWith("/leads/hygiene")) return "DeBounce Lead Hygiene & Buffers";
    if (path === "/leads") return "Lead Pipeline & Intake";
    if (path.startsWith("/svenskalaan/seo")) return "Svenskalaan SEO";
    if (path.startsWith("/premieportalen/campaigns") || path.startsWith("/smartresponse/campaigns")) return "PremiePortalen — Segmenteret Meta Campaign Builder";
    if (path.startsWith("/premieportalen/exclusions") || path.startsWith("/smartresponse/exclusions")) return "PremiePortalen — Eksklusionspuljer & Dublet-Motor";
    if (path.startsWith("/premieportalen/meta") || path.startsWith("/smartresponse/meta")) return "PremiePortalen — Meta Performance, Vindere & Alerts";
    if (path.startsWith("/premieportalen") || path.startsWith("/smartresponse")) return "PremiePortalen — Konkurrence-tabel, Link Cloaking & EventScheduler";
    return "Dashboard";
  };

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    if (location.pathname.startsWith("/campaigns")) {
      setIsCampaignHubOpen(true);
    }
    if (isEspRoute(location.pathname)) {
      setIsEspManagementOpen(true);
    }
    if (location.pathname.startsWith("/simplyloan")) {
      setIsSimplyLoanOpen(true);
      if (isSimplyLoanSecondaryRoute(location.pathname)) {
        setIsSimplyLoanMoreOpen(true);
      }
    }
    if (location.pathname.startsWith("/premieportalen") || location.pathname.startsWith("/smartresponse")) {
      setIsSmartResponseOpen(true);
    }
    if (location.pathname.startsWith("/accounting")) {
      setIsAccountingOpen(true);
    }
    if (isLegacyRoute(location.pathname)) {
      setIsLegacyOpen(true);
    }
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const linkClasses = (path: string) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(path)
      ? "bg-indigo-100 text-indigo-700"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

  const groupHeaderClasses = (active: boolean) =>
    `w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? "bg-indigo-100 text-indigo-700"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

  const subLinkClasses = (path: string) =>
    `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors pl-9 ${isActive(path)
      ? "bg-indigo-50 text-indigo-700"
      : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
    }`;

  // Shared sidebar content (used by both desktop and mobile)
  const sidebarContent = (
    <>
      {/* Logo Area */}
      <div className="h-16 flex items-center justify-center border-b overflow-hidden flex-shrink-0">
        <Link to="/" className="flex items-center justify-center h-full w-full">
          {logoError ? (
            <span className="text-2xl font-bold text-indigo-600">Afima</span>
          ) : (
            <img
              src="/afima-log-temp.png"
              alt="Afima"
              className="h-full w-auto object-contain scale-[2.5] origin-center"
              onError={() => setLogoError(true)}
            />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        <Link to="/tasks" className={linkClasses("/tasks")}>
          <ListTodo size={20} className="text-amber-500" />
          <span className="flex-1 font-semibold">Tasks & Actions</span>
          {pendingTaskCount > 0 && (
            <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
              {pendingTaskCount}
            </span>
          )}
        </Link>
        <Link to="/" className={linkClasses("/")}>
          <LayoutDashboard size={20} />
          <span className="font-semibold">Revenue Dashboard</span>
        </Link>
        <Link to="/overview" className={linkClasses("/overview")}>
          <Compass size={20} className="text-[#0F3F34]" />
          <span className="font-semibold">Daily Overview</span>
        </Link>
        <Link to="/email/daily-reports" className={linkClasses("/email/daily-reports")}>
          <BarChart3 size={20} />
          Daily Reports
        </Link>

        <div className="pt-4 pb-2">
          <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Email Operations
          </p>
        </div>

        {/* Campaign Hub (Collapsible, collapsed by default) */}
        <div>
          <div className={groupHeaderClasses(location.pathname.startsWith("/campaigns"))}>
            <Link to="/campaigns" className="flex items-center gap-2 flex-1 h-full" onClick={() => setIsCampaignHubOpen(true)}>
              <Library size={20} />
              <span>Campaign Hub</span>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsCampaignHubOpen(!isCampaignHubOpen); }}
              className="p-1 hover:bg-indigo-200 rounded text-gray-400 hover:text-indigo-800 transition-colors ml-2"
              aria-label="Toggle Campaign Hub submenu"
            >
              {isCampaignHubOpen ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          </div>
          {isCampaignHubOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/campaigns/text-library" className={subLinkClasses("/campaigns/text-library")}>
                <BookOpen size={16} />
                Text Library
              </Link>
              <Link to="/campaigns/affiliate-links" className={subLinkClasses("/campaigns/affiliate-links")}>
                <Link2 size={16} />
                Affiliate Links
              </Link>
            </div>
          )}
        </div>

        {/* Auto-Schedule (Single link — Scheduler Intelligence is a tab inside it) */}
        <Link
          to="/email/auto-schedule"
          className={
            location.pathname.startsWith("/email/auto-schedule") || location.pathname.startsWith("/email/scheduler-intelligence")
              ? "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors bg-indigo-100 text-indigo-700"
              : linkClasses("/email/auto-schedule")
          }
        >
          <Calendar size={20} />
          Auto-Schedule
        </Link>

        <Link to="/email/dispatch-matrix" className={linkClasses("/email/dispatch-matrix")}>
          <Table size={20} />
          Sendout Matrix
        </Link>

        {/* ESP & Deliverability (Collapsible, collapsed by default) */}
        <div>
          <button
            onClick={() => setIsEspManagementOpen(!isEspManagementOpen)}
            className={groupHeaderClasses(isEspRoute(location.pathname))}
          >
            <div className="flex items-center gap-2">
              <Server size={20} />
              <span>ESP & Deliverability</span>
            </div>
            {isEspManagementOpen ? (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {isEspManagementOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/email/esp-dashboard" className={subLinkClasses("/email/esp-dashboard")}>
                ESP KPI Center
              </Link>
              <Link to="/email/incoming" className={subLinkClasses("/email/incoming")}>
                Incoming Emails
              </Link>
            </div>
          )}
        </div>

        <div className="pt-4 pb-2">
          <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Portaler & Marketing
          </p>
        </div>

        {/* SimplyLoan (Collapsible, collapsed by default) */}
        <div>
          <div className={groupHeaderClasses(location.pathname.startsWith("/simplyloan"))}>
            <Link to="/simplyloan" className="flex items-center gap-2 flex-1 h-full" onClick={() => setIsSimplyLoanOpen(true)}>
              <Briefcase size={20} />
              <span>SimplyLoan</span>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSimplyLoanOpen(!isSimplyLoanOpen); }}
              className="p-1 hover:bg-indigo-200 rounded text-gray-400 hover:text-indigo-800 transition-colors ml-2"
              aria-label="Toggle SimplyLoan submenu"
            >
              {isSimplyLoanOpen ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          {isSimplyLoanOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/simplyloan" className={subLinkClasses("/simplyloan")}>
                Overview
              </Link>
              <Link to="/simplyloan/seo" className={subLinkClasses("/simplyloan/seo")}>
                🚀 SEO & Media Hub
              </Link>
              <Link to="/simplyloan/redirect-admin" className={subLinkClasses("/simplyloan/redirect-admin")}>
                🎯 Admin Module
              </Link>
              <Link to="/simplyloan/redirect-tracking" className={subLinkClasses("/simplyloan/redirect-tracking")}>
                📊 Tracking
              </Link>
              <Link to="/simplyloan/ltv" className={subLinkClasses("/simplyloan/ltv")}>
                <TrendingUp size={16} />
                Kohorte & Email LTV (24m)
              </Link>
              <Link to="/simplyloan/meta" className={subLinkClasses("/simplyloan/meta")}>
                <Target size={16} />
                Meta Ads
              </Link>
              <Link to="/simplyloan/meta/campaigns" className={subLinkClasses("/simplyloan/meta/campaigns")}>
                <Library size={16} />
                Meta campaigns
              </Link>
              <Link to="/simplyloan/email" className={subLinkClasses("/simplyloan/email")}>
                Email Marketing
              </Link>
              <Link to="/simplyloan/sms" className={subLinkClasses("/simplyloan/sms")}>
                SMS Marketing
              </Link>

              {/* Snoozed / Secondary Channels (Hidden by default inside SimplyLoan) */}
              <div className="pt-1">
                <button
                  onClick={() => setIsSimplyLoanMoreOpen(!isSimplyLoanMoreOpen)}
                  className="w-full flex items-center justify-between pl-9 pr-3 py-1 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <span>Other / Snoozed Channels</span>
                  {isSimplyLoanMoreOpen ? (
                    <ChevronDownIcon className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  )}
                </button>
                {isSimplyLoanMoreOpen && (
                  <div className="mt-1 space-y-1 border-l border-gray-100 ml-9 pl-2">
                    <Link to="/simplyloan/google" className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                      <TrendingUp size={14} />
                      Google Ads
                    </Link>
                    <Link to="/simplyloan/google/campaigns" className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                      <Library size={14} />
                      Google campaigns
                    </Link>
                    <Link to="/simplyloan/microsoft" className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                      <Activity size={14} />
                      Microsoft Ads
                    </Link>
                    <Link to="/simplyloan/microsoft/campaigns" className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                      <Library size={14} />
                      Microsoft campaigns
                    </Link>
                    <Link to="/simplyloan/optimizer" className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                      <ShieldCheck size={14} />
                      Autonomous Agent
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* PremiePortalen / Konkurrencer (Collapsible, collapsed by default) */}
        <div>
          <div className={groupHeaderClasses(location.pathname.startsWith("/premieportalen") || location.pathname.startsWith("/smartresponse"))}>
            <Link to="/premieportalen/portal" className="flex items-center gap-2 flex-1 h-full" onClick={() => setIsSmartResponseOpen(true)}>
              <Target size={20} />
              <span>PremiePortalen</span>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSmartResponseOpen(!isSmartResponseOpen); }}
              className="p-1 hover:bg-indigo-200 rounded text-gray-400 hover:text-indigo-800 transition-colors ml-2"
              aria-label="Toggle PremiePortalen submenu"
            >
              {isSmartResponseOpen ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          {isSmartResponseOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/premieportalen/portal" className={subLinkClasses("/premieportalen/portal")}>
                <Globe size={16} />
                Konkurrencer & Portal
              </Link>
              <Link to="/premieportalen/meta" className={subLinkClasses("/premieportalen/meta")}>
                <BarChart3 size={16} />
                Meta Performance
              </Link>
              <Link to="/premieportalen/campaigns" className={subLinkClasses("/premieportalen/campaigns")}>
                <Library size={16} />
                Campaign Builder
              </Link>
              <Link to="/premieportalen/exclusions" className={subLinkClasses("/premieportalen/exclusions")}>
                <ShieldCheck size={16} />
                Eksklusionspuljer
              </Link>
            </div>
          )}
        </div>

        {/* Svenskalaan SEO */}
        <Link to="/svenskalaan/seo" className={linkClasses("/svenskalaan/seo")}>
          <Globe size={20} />
          Svenskalaan.se SEO
        </Link>

        <div className="pt-4 pb-2">
          <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Admin & Finans
          </p>
        </div>

        {/* Accounting Hub (Collapsible, collapsed by default) */}
        <div>
          <div className={groupHeaderClasses(location.pathname.startsWith("/accounting"))}>
            <Link to="/accounting" className="flex items-center gap-2 flex-1 h-full" onClick={() => setIsAccountingOpen(true)}>
              <Wallet size={20} />
              <span>Accounting Hub</span>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsAccountingOpen(!isAccountingOpen); }}
              className="p-1 hover:bg-indigo-200 rounded text-gray-400 hover:text-indigo-800 transition-colors ml-2"
              aria-label="Toggle Accounting submenu"
            >
              {isAccountingOpen ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          </div>
          {isAccountingOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/accounting/invoices" className={subLinkClasses("/accounting/invoices")}>
                <FileSpreadsheet size={16} />
                Bilag Matcher
              </Link>
              <Link to="/accounting/renewals" className={subLinkClasses("/accounting/renewals")}>
                <CreditCard size={16} />
                Upcoming Renewals
              </Link>
              <Link to="/accounting/esp" className={subLinkClasses("/accounting/esp")}>
                <Server size={16} />
                ESP Costs
              </Link>
            </div>
          )}
        </div>

        {/* Lead Pipeline (Single link — DeBounce & Buffers is a tab inside it) */}
        <Link
          to="/leads"
          className={
            location.pathname.startsWith("/leads")
              ? "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors bg-indigo-100 text-indigo-700"
              : linkClasses("/leads")
          }
        >
          <Users size={20} />
          Lead Pipeline
        </Link>

        {/* Archived / Legacy Pages (Hidden by default at bottom so zero pages are lost) */}
        <div className="pt-4 mt-4 border-t border-gray-100">
          <button
            onClick={() => setIsLegacyOpen(!isLegacyOpen)}
            className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 rounded-md transition-colors"
          >
            <div className="flex items-center gap-2">
              <Archive size={15} />
              <span>Archived / Legacy Pages</span>
            </div>
            {isLegacyOpen ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </button>
          {isLegacyOpen && (
            <div className="mt-1 space-y-1">
              <Link to="/email/campaigns" className={subLinkClasses("/email/campaigns")}>
                Campaign Scheduler (Legacy)
              </Link>
              <Link to="/email/domain-rotation" className={subLinkClasses("/email/domain-rotation")}>
                Domain Rotation (FB)
              </Link>
              <Link to="/email/analytics" className={subLinkClasses("/email/analytics")}>
                <BarChart3 size={14} />
                Engagement Analytics
              </Link>
              <Link to="/email/campaign-stats" className={subLinkClasses("/email/campaign-stats")}>
                <BarChart2 size={14} />
                Campaign Stats
              </Link>
              <Link to="/email/imports" className={subLinkClasses("/email/imports")}>
                Imports (CSV)
              </Link>
              <Link to="/email/active-esps" className={subLinkClasses("/email/active-esps")}>
                Active ESPs (Legacy)
              </Link>
              <Link to="/converters" className={subLinkClasses("/converters")}>
                Converter Intelligence
              </Link>
            </div>
          )}
        </div>
      </nav>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-72 bg-white border-r flex-shrink-0 flex flex-col z-50 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Close button */}
        <button
          onClick={() => setIsMobileSidebarOpen(false)}
          className="absolute top-4 right-4 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 z-10"
        >
          <X size={20} />
        </button>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar — unchanged, hidden on mobile */}
      <aside className="w-64 bg-white border-r flex-shrink-0 flex-col fixed inset-y-0 z-10 hidden lg:flex">
        {sidebarContent}
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 lg:pl-64 transition-all duration-300">
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger menu — mobile only */}
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 lg:hidden flex-shrink-0"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
            <NotificationCenter />
            <ShareThoughtModal />
            <span className="text-sm text-gray-500 font-medium hidden lg:block">
              {currentTime}
            </span>
            <div className="flex items-center gap-2 sm:gap-3 pl-4 sm:pl-6 border-l border-gray-200">
              <div className="flex-col items-end hidden sm:flex">
                <span className="text-sm font-semibold text-gray-900 leading-none">{userName}</span>
                <span className="text-xs text-gray-500 mt-0.5">Afima</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                <UserCircle className="text-gray-400 w-full h-full" />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
